import { BeneficiaryType, VerifyPayeeCheckResponseCode } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { UpdateBeneficiaryRequestType } from 'PublicEntityTypes';
import { ComponentProps, useState } from 'react';
import { toast } from 'sonner';
import { usePayeeCheck } from '../../../../api/use-payee-check';
import { useData } from '../../../../contexts/data-context/use-data';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView, BeneficiaryView } from '../../../../types/entity-types';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { isPayeeCheckWarningRequired } from '../../../../utils/payee-check/is-payee-check-warning-required';
import { isVopCheckRequired } from '../../../../utils/payee-check/is-vop-check-required';
import { isVopPayeeCheck } from '../../../../utils/payee-check/is-vop-payee-check';
import { PayeeCheckConfirmationModal } from '../../components/payee-check-confirmation-modal';
import { PayeeCheckModal } from '../../components/payee-check-modal';
import { PaymentFlowStepWrapper } from '../../components/payment-flow-step-wrapper';
import { PayeeModalVariantConfig } from '../../components/payments-flow-modal';
import { ReviewStep } from '../../components/review-step';
import { EditBeneficiaryFlow } from '../../edit-beneficiary-flow/edit-beneficiary-flow';
import { PaymentDetails } from '../../payment-flow-storage';
import { usePaymentFlow } from '../../use-payment-flow';
import { getAccountDetails } from '../../utils/get-account-details';
import { getBeneficiaryName } from '../../utils/get-beneficiary-name';
import { isSupportedCurrency } from '../../utils/is-supported-currency';
import { InvoiceDetails, InvoicePaymentInformationWithoutToggle } from './invoice-payment-information-without-toggle';

type KnownBeneficiaryFlowSteps = 'INVOICE_INFORMATION' | 'EDIT_BENEFICIARY' | 'REVIEW';

type KnownBeneficiaryFlowProps = {
  accounts: AccountView[];
  beneficiaries: BeneficiaryView[];
  initialInvoiceDetails: InvoiceDetails;
  isLoading: boolean;
  checksVariantConfig?: ComponentProps<typeof InvoicePaymentInformationWithoutToggle>['checksVariantConfig'];
  onMakePayment: (paymentDetails: PaymentDetails) => Promise<void>;
  onCancelTransfer: () => void;
};

export const KnownBeneficiaryFlow = ({
  accounts,
  beneficiaries,
  initialInvoiceDetails,
  checksVariantConfig = { isVisible: false },
  isLoading,
  onMakePayment,
  onCancelTransfer,
}: KnownBeneficiaryFlowProps) => {
  const { isVopPaymentsLive } = useFlags<{
    isVopPaymentsLive: boolean;
  }>();
  const { mutations } = useData();
  const {
    getPaymentDetails,
    updatePaymentDetails,
    shouldRenderReviewStep,
    clearShouldRenderReviewStep,
    authorisationStatus,
    getEvidencePrefill,
  } = usePaymentFlow();
  const {
    mutations: { triggerUpdateBeneficiary, triggerVerifyPayee },
    isMutating: { isVerifyingPayee },
  } = usePayeeCheck();

  const [step, setStep] = useState<KnownBeneficiaryFlowSteps>(
    shouldRenderReviewStep ? 'REVIEW' : 'INVOICE_INFORMATION',
  );
  const [payeeCheckModalVariantConfig, setPayeeCheckModalVariantConfig] = useState<PayeeModalVariantConfig | undefined>(
    undefined,
  );
  const [payeeConfirmationModalVariantConfig, setPayeeConfirmationModalVariantConfig] = useState<
    PayeeModalVariantConfig | undefined
  >(undefined);
  const [isEditingBeneficiary, setIsEditingBeneficiary] = useState(false);

  const handleVopCheckResult = (code: VerifyPayeeCheckResponseCode, matchedName: string = '') => {
    switch (code) {
      case VerifyPayeeCheckResponseCode.MATCHED:
        setStep('REVIEW');

        return;
      case VerifyPayeeCheckResponseCode.CLOSE_MATCH:
        setPayeeCheckModalVariantConfig({ variant: code, name: matchedName });

        return;
      case VerifyPayeeCheckResponseCode.NO_MATCH:
      case VerifyPayeeCheckResponseCode.CHECK_NOT_POSSIBLE:
      case VerifyPayeeCheckResponseCode.INVALID:
      case VerifyPayeeCheckResponseCode.GENERAL:
      case VerifyPayeeCheckResponseCode.INTERNAL_ERROR:
        setPayeeCheckModalVariantConfig({ variant: code });

        return;
      default:
        return exhaustiveCheck(code);
    }
  };

  const closePayeeCheckModal = () => {
    setPayeeCheckModalVariantConfig(undefined);
  };

  const closePayeeConfirmationModal = () => {
    setPayeeConfirmationModalVariantConfig(undefined);
  };

  const closePayeeModal = (modalType: 'PAYEE_CHECK' | 'PAYEE_CONFIRMATION') => {
    switch (modalType) {
      case 'PAYEE_CHECK':
        closePayeeCheckModal();

        return;
      case 'PAYEE_CONFIRMATION':
        closePayeeConfirmationModal();

        return;
      default:
        return exhaustiveCheck(modalType);
    }
  };

  const onEditDetails = async (
    variantConfig: PayeeModalVariantConfig,
    modalType: 'PAYEE_CHECK' | 'PAYEE_CONFIRMATION',
    paymentDetails: ReturnType<typeof getPaymentDetails>,
  ) => {
    if (!paymentDetails) {
      Sentry.captureMessage(
        `Payment details are not available when calling onEditDetails in ${modalType} modal after ${step} step in EXTERNAL_PAYMENT flow`,
        {
          extra: {
            paymentDetails,
            modalType,
            step,
            beneficiaryIds: beneficiaries.map((b) => b.id),
          },
        },
      );

      return;
    }

    if (!paymentDetails.beneficiaryId) {
      Sentry.captureMessage(
        `Payment details beneficiaryId is not available when calling onEditDetails in ${modalType} modal after ${step} step in EXTERNAL_PAYMENT flow`,
        {
          extra: {
            paymentDetails,
            modalType,
            step,
            beneficiaryIds: beneficiaries.map((b) => b.id),
          },
        },
      );

      return;
    }

    const selectedBeneficiary = beneficiaries.find((b) => b.id === paymentDetails.beneficiaryId);

    if (!selectedBeneficiary) {
      Sentry.captureMessage(
        `Selected beneficiary not found when calling onEditDetails in ${modalType} modal after ${step} step in EXTERNAL_PAYMENT flow`,
        {
          extra: {
            paymentDetails,
            modalType,
            step,
            beneficiaryIds: beneficiaries.map((b) => b.id),
          },
        },
      );

      return;
    }

    if (variantConfig.variant !== VerifyPayeeCheckResponseCode.CLOSE_MATCH) {
      setStep('EDIT_BENEFICIARY');

      closePayeeModal(modalType);

      return;
    }

    try {
      setIsEditingBeneficiary(true);

      await triggerUpdateBeneficiary({
        urlSuffix: `/${paymentDetails.beneficiaryId}`,
        name: variantConfig.name,
      });

      await triggerVerifyPayee({
        paymentAccountId: paymentDetails.sourceAccountId,
        beneficiaryId: paymentDetails.beneficiaryId,
        name: variantConfig.name,
        iban: selectedBeneficiary.destination.iban,
      });

      await mutations.handleIncrementalUpdates();

      setStep('REVIEW');

      closePayeeModal(modalType);
    } catch (error) {
      Sentry.captureException(error);

      toast.error('Failed to update beneficiary');
    } finally {
      setIsEditingBeneficiary(false);
    }
  };

  switch (step) {
    case 'INVOICE_INFORMATION': {
      const paymentDetails = getPaymentDetails();
      const evidencePrefill = getEvidencePrefill();

      const invoiceDetails = {
        // Prioritize paymentDetails to display the most recent user input, falling back to invoiceDetails for initial values
        totalAmount: paymentDetails?.amount ?? initialInvoiceDetails.totalAmount,
        suggestedBeneficiaryId: paymentDetails?.beneficiaryId ?? initialInvoiceDetails.suggestedBeneficiaryId,
        invoiceReference: paymentDetails?.reference ?? initialInvoiceDetails.invoiceReference,
        currency: paymentDetails?.currency ?? initialInvoiceDetails.currency,
      };

      const onContinuePaymentForm = async () => {
        if (!paymentDetails) {
          Sentry.captureMessage(
            'Payment details are not available when calling onContinuePaymentForm in KnownBeneficiaryFlow',
            {
              extra: {
                paymentDetails,
              },
            },
          );

          return;
        }

        const selectedBeneficiary = beneficiaries.find((b) => b.id === paymentDetails.beneficiaryId);

        if (!selectedBeneficiary) {
          Sentry.captureMessage(
            'Selected beneficiary not found when calling onContinuePaymentForm in KnownBeneficiaryFlow',
            {
              extra: {
                beneficiaryId: paymentDetails.beneficiaryId,
                beneficiaryIds: beneficiaries.map((b) => b.id),
              },
            },
          );

          return;
        }

        const sourceAccount = accounts.find((a) => a.id === paymentDetails.sourceAccountId);

        if (!sourceAccount?.fspAdapter) {
          Sentry.captureMessage(
            'Source account fspAdapter cannot be retrieved when calling onContinuePaymentForm in KnownBeneficiaryFlow',
            {
              extra: {
                paymentDetails,
                accountIds: accounts.map((a) => a.id),
              },
            },
          );

          return;
        }

        const shouldTriggerVopCheck =
          isVopCheckRequired({
            currency: selectedBeneficiary.destination.currency,
            fspAdapter: sourceAccount.fspAdapter,
          }) && isVopPaymentsLive;

        if (!shouldTriggerVopCheck) {
          setStep('REVIEW');

          return;
        }

        if (shouldTriggerVopCheck) {
          try {
            const payeeCheck = await triggerVerifyPayee({
              paymentAccountId: paymentDetails.sourceAccountId,
              beneficiaryId: selectedBeneficiary.id,
              name: selectedBeneficiary.name,
              iban: selectedBeneficiary.destination.iban,
            });

            await mutations.handleIncrementalUpdates();

            if (isVopPayeeCheck(payeeCheck)) {
              if (
                isPayeeCheckWarningRequired({
                  previousCheck: selectedBeneficiary.latestPayeeCheck,
                  newCode: payeeCheck.code,
                })
              ) {
                handleVopCheckResult(payeeCheck.code, payeeCheck.matchedName);

                return;
              }
            }

            setStep('REVIEW');
          } catch (error) {
            Sentry.captureException(error);

            toast.error('Failed to verify payee');
          }
        }
      };

      return (
        <>
          <PaymentFlowStepWrapper onGoBack={onCancelTransfer} title="Invoice information">
            <InvoicePaymentInformationWithoutToggle
              accounts={accounts}
              initialSourceAccountId={paymentDetails?.sourceAccountId}
              beneficiaries={beneficiaries}
              invoiceDetails={invoiceDetails}
              evidencePrefill={evidencePrefill}
              isLoading={isLoading || isVerifyingPayee}
              checksVariantConfig={checksVariantConfig}
              onCancelTransfer={onCancelTransfer}
              onConfirmPaymentInformation={onContinuePaymentForm}
              onPaymentDataChange={(paymentValues) => {
                updatePaymentDetails({
                  sourceAccountId: paymentValues.sourceAccountId,
                  amount: paymentValues.amount,
                  currency: paymentValues.currency,
                  reference: paymentValues.paymentReference,
                  beneficiaryId: paymentValues.beneficiaryId,
                });
              }}
            />
          </PaymentFlowStepWrapper>

          {payeeCheckModalVariantConfig && (
            <PayeeCheckModal
              isOpen={!!payeeCheckModalVariantConfig}
              variantConfig={payeeCheckModalVariantConfig}
              isLoading={isEditingBeneficiary}
              onClose={closePayeeCheckModal}
              onEditDetails={() => onEditDetails(payeeCheckModalVariantConfig, 'PAYEE_CHECK', paymentDetails)}
              onContinue={() => {
                closePayeeCheckModal();
                setPayeeConfirmationModalVariantConfig(payeeCheckModalVariantConfig);
              }}
            />
          )}

          {payeeConfirmationModalVariantConfig && (
            <PayeeCheckConfirmationModal
              isOpen={!!payeeConfirmationModalVariantConfig}
              variantConfig={payeeConfirmationModalVariantConfig}
              isLoading={isEditingBeneficiary}
              onClose={closePayeeConfirmationModal}
              onEditDetails={() =>
                onEditDetails(payeeConfirmationModalVariantConfig, 'PAYEE_CONFIRMATION', paymentDetails)
              }
              onContinue={() => {
                closePayeeConfirmationModal();
                setStep('REVIEW');
              }}
            />
          )}
        </>
      );
    }
    case 'EDIT_BENEFICIARY': {
      const paymentDetails = getPaymentDetails();
      const sourceAccount = accounts.find((a) => a.id === paymentDetails?.sourceAccountId);

      if (!paymentDetails) {
        Sentry.captureMessage(
          'Payment details are not available when rendering the edit beneficiary step in KnownBeneficiaryFlow',
          {
            extra: {
              paymentDetails,
            },
          },
        );

        return null;
      }

      const selectedBeneficiary = beneficiaries.find((b) => b.id === paymentDetails.beneficiaryId);

      if (!selectedBeneficiary) {
        Sentry.captureMessage(
          'Selected beneficiary not found when rendering the edit beneficiary step in KnownBeneficiaryFlow',
          {
            extra: {
              paymentDetails,
              beneficiaryIds: beneficiaries.map((b) => b.id),
            },
          },
        );

        return null;
      }

      if (!sourceAccount?.fspAdapter) {
        Sentry.captureMessage(
          'Source account fspAdapter cannot be retrieved when rendering the edit beneficiary step in KnownBeneficiaryFlow',
          {
            extra: {
              paymentDetails,
              sourceAccountId: paymentDetails.sourceAccountId,
              sourceAccountIds: accounts.map((a) => a.id),
            },
          },
        );

        return;
      }

      // /\s+/ matches one or more whitespace characters (spaces, tabs, newlines, etc.)
      const [firstName, lastName = ''] = selectedBeneficiary.name.trim().split(/\s+/);

      return (
        <>
          <EditBeneficiaryFlow
            onGoBack={() => setStep('INVOICE_INFORMATION')}
            isLoading={isEditingBeneficiary}
            initialValues={{
              currency: selectedBeneficiary.destination.currency,
              country: selectedBeneficiary.country,
              iban: selectedBeneficiary.destination.iban,
              accountNumber: selectedBeneficiary.destination.accountNumber,
              sortCode: selectedBeneficiary.destination.sortCode,
              firstName,
              lastName,
              email: selectedBeneficiary.emailAddress,
            }}
            onSubmit={async (values) => {
              if (!sourceAccount?.fspAdapter) {
                Sentry.captureMessage(
                  'Source account fspAdapter cannot be retrieved when calling onSubmit in EditBeneficiaryFlow in KnownBeneficiaryFlow',
                );

                return;
              }

              const { detailsFormValues, accountType } = values;

              try {
                setIsEditingBeneficiary(true);

                const beneficiaryName = getBeneficiaryName({
                  legalName: detailsFormValues.legalName,
                  firstName: detailsFormValues.firstName,
                  lastName: detailsFormValues.lastName,
                });

                const body: UpdateBeneficiaryRequestType = {
                  name: beneficiaryName,
                  destination: {
                    type: detailsFormValues.iban ? BeneficiaryType.IBAN : BeneficiaryType.SCAN,
                    accountType,
                    currency: detailsFormValues.currency,
                    accountNumber: detailsFormValues.accountNumber,
                    sortCode: detailsFormValues.sortCode,
                    iban: detailsFormValues.iban,
                  },
                  country: detailsFormValues.country,
                  emailAddress: detailsFormValues.email,
                };

                const updatedBeneficiary = await triggerUpdateBeneficiary({
                  urlSuffix: `/${selectedBeneficiary.id}`,
                  ...body,
                });

                const shouldTriggerVopCheck = isVopCheckRequired({
                  currency: updatedBeneficiary.destination.currency,
                  fspAdapter: sourceAccount.fspAdapter,
                });

                if (!shouldTriggerVopCheck) {
                  await mutations.handleIncrementalUpdates();

                  toast.success('Beneficiary updated successfully');

                  setStep('REVIEW');

                  return;
                }

                const payeeCheckResult = await triggerVerifyPayee({
                  paymentAccountId: paymentDetails.sourceAccountId,
                  beneficiaryId: selectedBeneficiary.id,
                  name: beneficiaryName,
                  iban: detailsFormValues.iban,
                });

                await mutations.handleIncrementalUpdates();

                if (isVopPayeeCheck(payeeCheckResult)) {
                  if (
                    isPayeeCheckWarningRequired({
                      previousCheck: selectedBeneficiary.latestPayeeCheck,
                      newCode: payeeCheckResult.code,
                    })
                  ) {
                    handleVopCheckResult(payeeCheckResult.code, payeeCheckResult.matchedName);

                    toast.success('Beneficiary updated successfully');

                    return;
                  }
                }

                toast.success('Beneficiary updated successfully');

                setStep('REVIEW');
              } catch (error) {
                Sentry.captureException(error);

                toast.error('Failed to update beneficiary');
              } finally {
                setIsEditingBeneficiary(false);
              }
            }}
          />

          {payeeCheckModalVariantConfig && paymentDetails && (
            <PayeeCheckModal
              isOpen={!!payeeCheckModalVariantConfig}
              variantConfig={payeeCheckModalVariantConfig}
              isLoading={isEditingBeneficiary}
              onClose={closePayeeCheckModal}
              onEditDetails={() => onEditDetails(payeeCheckModalVariantConfig, 'PAYEE_CHECK', paymentDetails)}
              onContinue={() => {
                closePayeeCheckModal();
                setPayeeConfirmationModalVariantConfig(payeeCheckModalVariantConfig);
              }}
            />
          )}

          {payeeConfirmationModalVariantConfig && paymentDetails && (
            <PayeeCheckConfirmationModal
              isOpen={!!payeeConfirmationModalVariantConfig}
              variantConfig={payeeConfirmationModalVariantConfig}
              isLoading={isEditingBeneficiary}
              onClose={closePayeeConfirmationModal}
              onEditDetails={() =>
                onEditDetails(payeeConfirmationModalVariantConfig, 'PAYEE_CONFIRMATION', paymentDetails)
              }
              onContinue={() => {
                closePayeeConfirmationModal();
                setStep('REVIEW');
              }}
            />
          )}
        </>
      );
    }
    case 'REVIEW': {
      const paymentDetails = getPaymentDetails();

      if (!paymentDetails) {
        Sentry.captureMessage(
          'Payment details are not available when rendering the review step in KnownBeneficiaryFlow',
          {
            extra: {
              paymentDetails,
              initialInvoiceDetails,
              authorisationStatus,
              step,
              message: 'Payment details are not available when rendering the review step in KnownBeneficiaryFlow',
            },
          },
        );

        return null;
      }

      const { amount, currency, reference, sourceAccountId, beneficiaryId } = paymentDetails;

      if (!reference) {
        Sentry.captureMessage('Reference is not available when rendering the review step in KnownBeneficiaryFlow', {
          extra: { paymentDetails, step },
        });

        return null;
      }

      const destinationBeneficiary = beneficiaries.find((b) => b.id === beneficiaryId);

      if (!destinationBeneficiary) {
        Sentry.captureMessage(
          'Destination beneficiary not found when rendering the review step in KnownBeneficiaryFlow',
          {
            extra: {
              paymentDetails,
              initialInvoiceDetails,
              beneficiaries: beneficiaries.map((b) => b.id),
              beneficiaryId,
              authorisationStatus,
              step,
              message: 'Destination beneficiary not found when rendering the review step in KnownBeneficiaryFlow',
            },
          },
        );

        return null;
      }

      if (!isSupportedCurrency(currency)) {
        Sentry.captureMessage('Unsupported currency when rendering the review step in KnownBeneficiaryFlow', {
          extra: {
            paymentDetails,
            initialInvoiceDetails,
            currency,
            authorisationStatus,
            step,
            message: 'Unsupported currency when rendering the review step in KnownBeneficiaryFlow',
          },
        });

        return null;
      }

      const { type, accountNumber, sortCode, iban } = destinationBeneficiary.destination;

      const sourceAccount = accounts.find((a) => a.id === sourceAccountId);
      const beneficiaryAccountDetails = getAccountDetails({
        type,
        accountNumber,
        sortCode,
        iban,
      });

      return (
        <PaymentFlowStepWrapper
          onGoBack={() => {
            setStep('INVOICE_INFORMATION');
            clearShouldRenderReviewStep();
          }}
          title="Review"
        >
          <Stack space={3} align="stretch">
            <ReviewStep
              name={destinationBeneficiary.name}
              amount={getAmountWithCurrency({ amount, currency, isAccountBalance: true })}
              currency={currency}
              accountDetails={beneficiaryAccountDetails}
              latestPayeeCheck={
                isVopPayeeCheck(destinationBeneficiary.latestPayeeCheck)
                  ? destinationBeneficiary.latestPayeeCheck.code
                  : undefined
              }
              bankName={sourceAccount?.institution?.name}
              bankLogo={sourceAccount?.institution?.logo}
              reference={reference}
              isLoading={isLoading}
              onConfirm={() => onMakePayment(paymentDetails)}
            />
          </Stack>
        </PaymentFlowStepWrapper>
      );
    }
    default:
      return exhaustiveCheck(step);
  }
};
